import React, { useEffect, useMemo, useState } from 'react';
import { useEffectOnce } from 'react-use';
import { useRouter } from 'next/router';
import { useQuery } from 'urql';

import Breadcrumbs from '@/components/Breadcrumbs';
import GalleryCarousel from '@/components/GalleryCarousel';
import GiftCardOrderBlock from '@/components/GiftCardOrderBlock';
import HeroImage from '@/components/layout/HeroImage';
import PlaceHolderImage from '@/components/PlaceHolderImage';
import ShareBlock from '@/components/ShareBlock';
import query from '@/graphql/queries/user.graphql';
import useIsMobile from '@/hooks/useIsMobile';
import useStore from '@/hooks/useStore';
import { trackImpression } from '@/lib/GA';
import { ErrorPage } from '@/pages/_error';

import CouponIcon from '@/images/icons/coupon.svg';

import styles from '../styles.module.scss';

interface Props {
  giftcard: GiftCardFragment;
}

const Product = ({ giftcard }: Props) => {
  const [hasInit, setHasInit] = useState(false);
  const { exclusive, uuid } = useStore('userStore');
  const { getProduct } = useStore('cart');
  const [isMobile] = useIsMobile(768);

  const router = useRouter();
  const product = getProduct(router.query.slug! as string);

  const [{ data, fetching, error }] = useQuery<UserQuery>({
    query,
    variables: {
      uuid,
      slug: router.query.slug,
      exclusive,
    },
  });

  useEffect(() => {
    if (!hasInit && product && product.id && product.title && !fetching) {
      trackImpression({
        id: product.id,
        name: product.title,
        category: 'Gift Cards',
        // price: data.product!.defaultPrice!,
      });
      setHasInit(true);
    }
  }, [product, fetching, hasInit]);

  useEffectOnce(() => {
    if (typeof window !== 'undefined') {
      document.body.setAttribute('data-static-bottom-padding-small', isMobile?.toString());
    }

    return () => {
      document.body.removeAttribute('data-static-bottom-padding-small');
    };
  });

  // Checks if item is already within in the wishlist
  const itemInWishlist = useMemo(
    () => !!data?.userDetails?.wishlist?.filter((w) => w?.id === product?.id).length,
    [product?.id, data?.userDetails?.wishlist],
  );

  if (!product) {
    return <ErrorPage statusCode={404} err={error} />;
  }

  const breadcrumbs: BreadcrumbItem[] = [
    { title: 'Online Shop', uri: '/shop' },
    {
      title: 'Gift Cards',
      uri: '/shop/gift-cards',
    },
    { title: product.title! },
  ];

  const image: ImageObject[] = [
    {
      width: 717,
      height: 452,
      jpg: giftcard?.image?.length
        ? giftcard?.image[0]?.url || ''
        : product?.bh?.productConfigurations?.[0].productImages?.[0]?.frontImage || '',
    },
  ];

  return (
    <>
      <div className={styles.product} data-testid="giftCardPage">
        <HeroImage />
        <div className={styles.inner}>
          <h1>{product.title}</h1>
          <Breadcrumbs breadcrumbs={breadcrumbs} />
          <p className={styles.offerDiscount}>
            {product.bh!.description && <CouponIcon />}
            {product.title}
          </p>
          <div className={styles.content}>
            <div className={styles.imageContainer}>
              {image[0].jpg ? <GalleryCarousel images={image} /> : <PlaceHolderImage />}
            </div>
            <div className={styles.addToCart}>
              <GiftCardOrderBlock product={product} inWishlist={itemInWishlist} />
            </div>
            {product?.bh?.description && (
              <p className={styles.body} dangerouslySetInnerHTML={{ __html: product?.bh?.description }} />
            )}
            <div className={styles.TermsAndConditions}>
              <h4> Terms and Conditions </h4>
              <p dangerouslySetInnerHTML={{ __html: product?.bh?.termsAndConditions.termsAndConditions || '' }} />
            </div>

            {isMobile && <ShareBlock />}
          </div>
        </div>
      </div>
    </>
  );
};

export default Product;
